import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useGoServerStore } from '../stores/goServerStore';
import { useSoundEffects } from '../hooks/useSoundEffects';
import { useOnboardingStore } from '../features/onboarding/store/onboardingStore';

const TranscriptionServerStatus: React.FC = () => {
    const { i18n } = useTranslation();
    const connectionStatus = useGoServerStore(s => s.connectionStatus);
    const startPeriodicConnectionCheck = useGoServerStore(s => s.startPeriodicConnectionCheck);
    const checkConnection = useGoServerStore(s => s.checkConnection);
    const [isVisible, setIsVisible] = React.useState(true);
    const [isTourNoticeActive, setIsTourNoticeActive] = React.useState(false);
    const isOnboardingOpen = useOnboardingStore((state) => state.isOpen);

    const { playErrorSound } = useSoundEffects();
    const hasPlayedSoundRef = React.useRef(false);

    useEffect(() => {
        checkConnection();
        const stopCheck = startPeriodicConnectionCheck();
        return () => stopCheck();
    }, [startPeriodicConnectionCheck, checkConnection]);

    useEffect(() => {
        if (!connectionStatus.isConnected && connectionStatus.lastChecked) {
            setIsVisible(true);
            if (!hasPlayedSoundRef.current) {
                playErrorSound();
                hasPlayedSoundRef.current = true;
            }
        } else if (connectionStatus.isConnected) {
            hasPlayedSoundRef.current = false;
        }
    }, [connectionStatus.isConnected, connectionStatus.lastChecked, playErrorSound]);

    useEffect(() => {
        const handleTourStepChange = (e: Event) => {
            const { stepId } = (e as CustomEvent<{ stepId: string | null }>).detail;
            setIsTourNoticeActive(stepId === 'live-setup-server-notice');
        };
        window.addEventListener('closer-tour-step-change', handleTourStepChange);
        return () => window.removeEventListener('closer-tour-step-change', handleTourStepChange);
    }, []);

    if (!isTourNoticeActive && (connectionStatus.isConnected || !connectionStatus.lastChecked || !isVisible || isOnboardingOpen)) {
        return null;
    }

    return (
        <div
            className="fixed top-4 left-4 z-[9999] w-full max-w-sm"
            data-tour-id="transcription-server-status"
            dir="rtl"
        >
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl border border-red-200 dark:border-red-700 overflow-hidden">
                    <div className="bg-red-500 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <ExclamationTriangleIcon className="h-5 w-5 text-white flex-shrink-0" />
                            <h3 className="text-white font-bold text-sm">פתיחת התוכנה</h3>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsVisible(false)}
                            disabled={isTourNoticeActive}
                            className={`text-white/80 p-1 rounded-md transition-colors ${isTourNoticeActive ? 'cursor-default' : 'hover:text-white hover:bg-white/20'}`}
                            aria-label="סגור"
                        >
                            <XMarkIcon className="h-4 w-4 shrink-0 pointer-events-none" />
                        </button>
                    </div>

                    <div className="bg-gray-50 dark:bg-neutral-700 p-4 flex flex-col gap-3">
                        <p className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
                            אם מופיע לך הודעה אדומה בפינת המסך, יש ללחוץ על הקישור בהודעה לפתוח את Closer.AI במחשב.
                            <br />
                            התוכנה חייבת לפעול ברקע בכל שימוש במערכת.
                        </p>

                        {isTourNoticeActive ? (
                            <span className="text-sm font-bold text-red-600 underline dark:text-red-400 w-fit cursor-default">
                                לסרטון "כיצד לפתוח את תוכנת קלוזר" לחץ כאן &gt;&gt;
                            </span>
                        ) : (
                            <a
                                href="https://www.youtube.com/watch?v=A_MNfop0LDA"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-bold text-red-600 underline hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 w-fit"
                            >
                                לסרטון "כיצד לפתוח את תוכנת קלוזר" לחץ כאן &gt;&gt;
                            </a>
                        )}

                        <div className="flex flex-col gap-1">
                            <button
                                type="button"
                                onClick={() => { if (!isTourNoticeActive) window.location.href = 'closeraudio://open'; }}
                                disabled={isTourNoticeActive}
                                className={`px-3 py-1.5 text-xs font-semibold bg-red-100 text-red-700 rounded-md transition-colors w-fit border border-red-200 dark:bg-red-900/40 dark:border-red-700 dark:text-red-300 ${isTourNoticeActive ? 'cursor-default' : 'hover:bg-red-200 dark:hover:bg-red-900/60'}`}
                            >
                                {i18n.t('agentConfig.launchServer')}
                            </button>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                * {i18n.t('agentConfig.runOnceWarning')}
                            </span>
                        </div>
                    </div>
                </div>
        </div>
    );
};

export default TranscriptionServerStatus;
